import { Outlet, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import useRefreshToken from "../common/hooks/useRefreshToken";
import useAuth from "../common/hooks/useAuth";
import { Grid, CircularProgress, Typography, Link } from "@mui/material";
import { TEXT_MSGS } from "../common/utility/constant";
import LoginModal from "./landing/sections/LoginPopupNew";
import {
    SESSION_LOGIN_EVENT,
    SESSION_LOGOUT_EMAIL_KEY,
    resolveSessionLoginMessage,
    resolveSessionLoginLastLogin,
} from "../common/api/hooks/useAxiosPrivate";

const PersistLogin = () => {
    const [isLoading, setIsLoading] = useState(true);
    const refresh = useRefreshToken();
    const { auth, persist } = useAuth();
    const [apiError, setAPIError] = useState(undefined);
    const [goLanding, setGoLanding] = useState(false);
    const [sessionLoginOpen, setSessionLoginOpen] = useState(false);
    const [sessionLoginMsg, setSessionLoginMsg] = useState(
        TEXT_MSGS.LOGIN_POPUP_MSG,
    );
    const [sessionLoginLastLogin, setSessionLoginLastLogin] =
        useState(undefined);

    const [online, setOnline] = useState(window.navigator.onLine);

    const closeSessionLoginModal = () => {
        setSessionLoginOpen(false);
        setSessionLoginLastLogin(undefined);
    };

    useEffect(() => {
        const openModal = (detail) => {
            if (typeof detail === "string") {
                setSessionLoginMsg(detail || TEXT_MSGS.LOGIN_POPUP_MSG);
                setSessionLoginLastLogin(undefined);
            } else if (detail == null) {
                setSessionLoginMsg(TEXT_MSGS.LOGIN_POPUP_MSG);
                setSessionLoginLastLogin(undefined);
            } else {
                setSessionLoginMsg(resolveSessionLoginMessage(detail));
                setSessionLoginLastLogin(
                    detail.lastLogin ?? resolveSessionLoginLastLogin(detail),
                );
            }
            setSessionLoginOpen(true);
        };

        const onSessionLogin = (event) => {
            openModal(event.detail);
        };

        const onStorage = (event) => {
            if (event.key !== SESSION_LOGOUT_EMAIL_KEY || !event.newValue) {
                return;
            }

            try {
                const logoutEvent = JSON.parse(event.newValue);
                const myEmail = String(auth?.email_id ?? "")
                    .trim()
                    .toLowerCase();

                if (myEmail && logoutEvent.email === myEmail) {
                    openModal(TEXT_MSGS.LOGIN_POPUP_MSG);
                }
            } catch {
                // Ignore malformed storage events.
            }
        };

        window.addEventListener(SESSION_LOGIN_EVENT, onSessionLogin);
        window.addEventListener("storage", onStorage);

        return () => {
            window.removeEventListener(SESSION_LOGIN_EVENT, onSessionLogin);
            window.removeEventListener("storage", onStorage);
        };
    }, [auth?.email_id]);

    useEffect(() => {
        let isMounted = true;

        const verifyRefreshToken = async () => {
            try {
                setAPIError(undefined);
                await refresh();
            } catch (err) {
                const status = err?.response?.status;

                if (status === 410) {
                    setSessionLoginMsg(resolveSessionLoginMessage(err));
                    setSessionLoginLastLogin(resolveSessionLoginLastLogin(err));
                    setSessionLoginOpen(true);
                } else if (status === 401 || status === 403) {
                    if (isMounted) {
                        setGoLanding(true);
                    }
                } else if (isMounted) {
                    setAPIError(err);
                }
            } finally {
                isMounted && setIsLoading(false);
            }
        };

        !auth?.accessToken ? verifyRefreshToken() : setIsLoading(false);

        return () => {
            isMounted = false;
        };
    }, []);

    if (goLanding) {
        return <Navigate to="/landing" replace />;
    }

    if (sessionLoginOpen && !auth?.accessToken) {
        return (
            <LoginModal
                open
                variant="sessionExpired"
                msg={sessionLoginMsg}
                lastLogin={sessionLoginLastLogin}
                onClose={closeSessionLoginModal}
            />
        );
    }

    return (
        <>
            {sessionLoginOpen ? (
                <LoginModal
                    open
                    variant="sessionExpired"
                    msg={sessionLoginMsg}
                    lastLogin={sessionLoginLastLogin}
                    onClose={closeSessionLoginModal}
                />
            ) : null}

            {apiError == undefined ? (
                <>
                    {!persist ? (
                        <Outlet />
                    ) : isLoading ? (
                        <Grid
                            component="main"
                            container
                            display="flex"
                            flexDirection="column"
                            justifyContent="center"
                            alignItems="center"
                            sx={{ height: `calc(100vh - 64px)` }}
                        >
                            <div className="flexGrow">
                                <CircularProgress />
                            </div>
                        </Grid>
                    ) : (
                        <Outlet />
                    )}
                </>
            ) : (
                <Grid
                    component="main"
                    container
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    sx={{ height: `calc(100vh - 64px)` }}
                >
                    {online ? (
                        <Typography
                            variant="warningText"
                            sx={{ fontSize: 20, cursor: "default" }}
                        >
                            {apiError?.response?.data?.message
                                ? apiError?.response?.data?.message
                                : TEXT_MSGS.NETWORK_ERROR_MSG}
                        </Typography>
                    ) : (
                        <Typography
                            variant="warningText"
                            sx={{ fontSize: 20, cursor: "default" }}
                        >
                            {TEXT_MSGS.INTERNET_ERROR_MESSAGE}
                        </Typography>
                    )}

                    <div className="flexGrow">
                        <Link
                            sx={{ mt: 2, pt: 2, cursor: "pointer" }}
                            onClick={() => window.location.reload()}
                            variant="link"
                        >
                            Refresh Page
                        </Link>
                    </div>
                </Grid>
            )}
        </>
    );
};

export default PersistLogin;
